/**
 * SHA-256 of a blob, lowercase hex.
 *
 * ## Why this is shared and not a helper inside one uploader
 *
 * `POST /files/{id}/confirm` requires `checksumSha256` — it is `@NotBlank` on the server,
 * so a confirm without it is a 400 and the reserved file is never marked complete. Three
 * uploaders in this app called `confirmUpload(fileId)` with no body and all three broke
 * the same way, while a fourth (`polling/AudioRecorder`) had a private copy of this
 * function and worked. One implementation, imported everywhere, is what stops that
 * happening a fifth time.
 *
 * ## What the checksum is FOR
 *
 * The backend stores it and does **not** verify it — verifying would mean streaming the
 * object back out of storage on every upload. It is the client's record of what it
 * believes it sent, so a later integrity question has something to compare against. That
 * is only worth anything if it is computed over the exact bytes that were PUT, so hash the
 * same `Blob`/`File` object you upload, never a re-read of it.
 *
 * `crypto.subtle` needs a secure context — https, or localhost. On an insecure origin it
 * is undefined, which is a deployment problem rather than something to paper over here.
 */
export async function sha256Hex(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer()
  const digest = await crypto.subtle.digest('SHA-256', buffer)
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}
